import Route from '@ember/routing/route'
import { service } from '@ember/service'
import { TrackedArray } from 'tracked-built-ins'
import { isEmpty } from '@ember/utils'
import { task } from 'ember-concurrency'
import type Store from '@ember-data/store'
import type Teacher from 'teacher-ui-client/models/teacher'
import { uniq } from 'lodash'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type { FluencySprintsTeacherItem } from 'teacher-ui-client/components/tables/fluency-sprints/teachers'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import type { StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import { hasId } from 'teacher-ui-client/utils/type-guards'

const MAX_CONCURRENCY = 3

export default class extends Route {
  queryParams = {
    sort: {
      replace: true,
    },
  }

  @service studentEvents!: StudentEventsService
  @service gradeSets!: GradeSetsService
  @service store!: Store

  async model() {
    const loadTeacherDataTask = this.loadTeacherData
    void loadTeacherDataTask.cancelAll()

    const teacherRecords = await this.store.query<Teacher>('teacher', {
      // @ts-expect-error - include types not correct
      include: 'school-classes,school-classes.students',
    })

    // Immediately return an empty array for teachers. For each teacher record, we'll load extra teacher data
    // progressively and add to the model as it comes in, in a non-blocking asynchronous way. As each item is added to
    // the model, it will then display in the table.
    const teacherItems: TrackedArray<FluencySprintsTeacherItem> = new TrackedArray([])
    teacherRecords.forEach((teacherRecord) => {
      void this.loadTeacherData.perform(teacherRecord, teacherItems)
      return
    })
    return { teacherItems, loadTeacherDataTask }
  }

  /**
   * With a given teacher record, load the aggregate data and add it to the given teachers array
   */
  loadTeacherData = task(
    { enqueue: true, maxConcurrency: MAX_CONCURRENCY },
    async (teacherRecord: Teacher, teacherItems: TrackedArray<FluencySprintsTeacherItem>) => {
      const { gradeSets } = this

      const students = teacherRecord.studentsForSubscriptionType(SubscriptionType.Maths).filter(hasId)
      if (isEmpty(students) || !teacherRecord.id) return // Skip teachers with no maths students.

      const uniqStudentGrades = uniq(students.map((student) => student.gradePosition))

      // Grades
      const grades = uniqStudentGrades
        .sort((a, b) => a - b)
        .map((gradePosition) => gradeSets.nameFromGradeSetByPosition(gradePosition))
        .join(', ')

      // Get mental minute aggregate data for the teachers' students
      const scope: StudentsScope = { scope: 'students', ids: students.map((student) => student.id) }
      const { period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

      const overviewData = await this.studentEvents.mentalMinuteOverview('maths', scope, period)
      if (!overviewData) return
      const aggregate = overviewData.aggregate

      const teacherData = {
        id: teacherRecord.id,
        firstName: teacherRecord.firstName,
        lastName: teacherRecord.lastName,
        grades,
        aggregate,
      }

      // Push the teacher data to the array
      teacherItems.push({ ...teacherData })
    },
  )
}
